import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { ProductContext } from '../../ProductProvider';
import { useSnackbar } from 'notistack';
import { Table, Button, Modal, Breadcrumb } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { Link } from "react-router-dom";
import { fetchAuthSession } from 'aws-amplify/auth';
import AddGallery from './AddGallery';
import EditGallery from './EditGallery';
import logo from '../../assets/images/logo/COLLEGELOGO.jpeg';
import GalleryImageLoader from './GalleryImageLoader';
import Preloader from '../../components/Preloader/Preloader';
import '../../styles.css';

const config = require('../../config.json');

export default function GalleryViewer() {
    const [loaded, setLoaded] = useState(false);
    const { validateLogin, loggedUser } = useContext(ProductContext);
    const [galleryImages, setGalleryImages] = useState([]);
    const [viewImage, setViewImage] = useState(false);
    const [addImage, setAddImage] = useState(false);
    const [editImage, setEditImage] = useState(false);
    const [displayImages, setDisplayImages] = useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const [imageFile, setImageFile] = useState('');
    const [profileImage, setProfileImage] = useState(logo);
    const [keyName, setKeyName] = useState('');
    const [projectName, setProjectName] = useState('');
    const [projectDate, setProjectDate] = useState('');
    const [newProjectName, setNewProjectName] = useState('');
    const [newProjectSize, setNewProjectSize] = useState('');
    const [newProjectStatus, setNewProjectStatus] = useState('');
    const [newProjectDate, setNewProjectDate] = useState('');
    const [selectedProject, setSelectedProject] = useState('');

    useEffect(() => {
      if (loggedUser.isAuthenticated && loggedUser.profile === "Super Student") {
        fetchGalleryImages();
      } else {
        validateLogin();
      }
    }, [loggedUser]);

    const handleOpenImageView = () => setViewImage(true);
    const handleCloseImageView = () => setViewImage(false);
    const handleOpenImageAdd = () => setAddImage(true);
    const handleCloseImageAdd = () => setAddImage(false);
    const handleOpenImageEdit = () => setEditImage(true);
    const handleCloseImageEdit = () => setEditImage(false);

    const handleProjectEdit = (event, id) => {
      event.preventDefault();
      const itemtest = galleryImages.filter((image) => image.id === id);
      if (itemtest.length > 0) {
        const item = itemtest[0];
        setKeyName(item.folderName);
        setProjectName(item.projectName);
        setProjectDate(item.projectDate);
        setNewProjectName(item.projectName);
        setNewProjectStatus(item.projectStatus);
        setNewProjectSize(item.projectSize);
        setNewProjectDate(item.projectDate);
        setSelectedProject(item);
        handleOpenImageEdit();
      }
    };

    const handleProjectDelete = async (event, id) => {
      event.preventDefault();
      const itemtest = galleryImages.filter((image) => image.id === id);
      if (itemtest.length === 0) return;
      const item = itemtest[0];
      try {
        const inputParams = { fileName: item };
        const { idToken } = (await fetchAuthSession()).tokens ?? {};

        await axios.post(`${config.api.invokeURL}/gallery/delgallery`, inputParams, {
          headers: {
              Authorization: idToken,
              'x-api-key': config.api.key
          }
        }).then(() => {
          fetchGalleryImages();
          enqueueSnackbar("Gallery album successfully deleted", { variant: 'success' });
        }).catch((error) => {
          enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
        });
      } catch (error) {
        enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
      }
    };

    const postOnS3 = async () => {
      try {
        if (!newProjectDate || !newProjectName || !imageFile) {
          enqueueSnackbar("Please enter all details and attach an image", { variant: 'info' });
          return;
        }        
        setLoaded(false);
        const fileName = newProjectName + "_" + new Date().getTime();
        const newFileName = fileName + ".jpeg";
        const newKeyName = newProjectName + "$" + newProjectDate;
        const tKeyName = "/images/gallery/" + newKeyName;
        const bucketName = config.s3.bucketName + tKeyName;
        const file = imageFile;
        const blob = file.slice(0, file.size, 'image/jpeg'); 
        const newFile = new File([blob], newFileName, { type: 'image/jpeg' });
        const { idToken } = (await fetchAuthSession()).tokens ?? {};

        await axios.get(`${config.api.invokeURL}/gallery/newgallery?fileName=${newFileName}&bucketName=${bucketName}&keyName=${newKeyName}`, {
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        }).then(async (response) => {
          enqueueSnackbar("Gallery image uploaded", { variant: 'success' });
          handleCloseImageAdd();
          fetchGalleryImages();
          setLoaded(true);
          await fetch(response.data.uploadURL, {
            method: 'PUT',
            headers: { "Content-Type": "image/jpeg" },
            body: newFile
          });
          setProfileImage(newFileName);
        }).catch((error) => {
          enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
          setLoaded(true);
        });
      } catch (error) {
        enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
        setLoaded(true);
      }
    };

    const editOnS3 = async () => {
      try {
        if (!newProjectDate || !newProjectName) {
          enqueueSnackbar("Please enter all project details", { variant: 'info' });
          return;
        }
        setLoaded(false);
        const newKeyName = newProjectName + "$" + newProjectDate;
        const inputParams = {
          fileName: selectedProject,
          newKeyName: newKeyName
        };
        const { idToken } = (await fetchAuthSession()).tokens ?? {};
        
        await axios.post(`${config.api.invokeURL}/gallery/editgallery`, inputParams, {
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        }).then(() => {
          enqueueSnackbar("Gallery album updated successfully", { variant: 'success' });
          setLoaded(true);
          handleCloseImageEdit();
          fetchGalleryImages();
        }).catch((error) => {
          enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
          setLoaded(true);
        });
      } catch (error) {
        enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
        setLoaded(true);
      }
    };

    const fetchGalleryImages = async () => {
      try {
        setLoaded(false);
        const fileName = 'images/gallery/';
        const bucketName = "images.vidyajyoticollege.in";
        await axios.get(`${config.api.invokeURL}/gallery?fileName=${fileName}&bucketName=${bucketName}`, {
          headers: {
            'x-api-key': config.api.key
          }
        }).then((response) => {
          processGalleryData(response.data);
          enqueueSnackbar(`${response.data.length} Gallery albums loaded`, { variant: 'success' });
        }).catch((error) => {
          enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
          setLoaded(true);
        });
      } catch (error) {
        enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
        setLoaded(true);
      }
    };

    const processGalleryData = (item) => {
      const test = [];
      for (const i in item) {
        const testv = { ...item[i] };
        testv.length = item[i].fileName ? item[i].fileName.length : 0;
        testv.id = parseInt(i) + 1;
        test.push(testv);
      }
      setGalleryImages(test);
      setLoaded(true);
    };

    const handleProjectView = async (e, id) => {
      e.preventDefault();
      const itemtest = galleryImages.filter((image) => image.id === id);
      if (itemtest.length === 0) return;
      const item = itemtest[0];
      setKeyName(item.folderName);
      setProjectName(item.projectName);
      setProjectDate(item.projectDate);
      setNewProjectName(item.projectName);
      setNewProjectStatus(item.projectStatus);
      setNewProjectSize(item.projectSize);
      setNewProjectDate(item.projectDate);
      setSelectedProject(item);

      const tfoldername = item.folderName ? item.folderName.substring(0, item.folderName.length - 1) : '';
      try {
        setLoaded(false);
        const bucketName = "images.vidyajyoticollege.in";
        await axios.get(`${config.api.invokeURL}/gallery/slider?fileName=${tfoldername}&bucketName=${bucketName}`, {
          headers: {
            'x-api-key': config.api.key
          }
        }).then((response) => {
          const finalList = [];
          if (response.data && response.data.length > 0) {
            for (const i in response.data) {
              const tValue = response.data[i];
              const tPathName = tValue.split("/");
              const tBreakUp = tPathName[2];
              const tempValue = {};
              tempValue.fullPathName = response.data[i];
              tempValue.fileName = tBreakUp;
              finalList.push(tempValue);
            }
          }
          setDisplayImages(finalList);
          handleOpenImageView();
          setLoaded(true);
        }).catch((error) => {
          enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
          setLoaded(true);
        });
      } catch (error) {
        enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
        setLoaded(true);
      }
    };

    return (
      <div className="gallery-viewer-page animate-fade-in-up">
        <Preloader show={!loaded} />
        <Helmet>
          <title>Website Gallery Management - Vidyajyoti College</title>
          <meta name="description" content="Manage college campus photo galleries and albums" />
          <link rel="canonical" href="/gallery-viewer" />
        </Helmet>

        {/* Breadcrumb */}
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to="/">
              <i className="fa fa-home" aria-hidden="true"></i> Dashboard
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Websites</Breadcrumb.Item>
          <Breadcrumb.Item active>Gallery</Breadcrumb.Item>
        </Breadcrumb>

        <div className="page-title-section">
          <div className="page-title-wrapper">
            <h4>
              <i className="fa fa-picture-o text-primary"></i> Gallery Albums
            </h4>
            <div className="page-subtitle">
              Manage photo collections and event archives
            </div>
          </div>

          <Button 
            variant="primary" 
            size="sm" 
            onClick={handleOpenImageAdd}
            className="d-inline-flex align-items-center gap-1"
          >
            <i className="fa fa-plus"></i> Create New Gallery
          </Button>
        </div>

        {/* View Modal */}
        <Modal show={viewImage} onHide={handleCloseImageView} size="xl" centered>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">
              <i className="fa fa-picture-o text-primary me-2"></i> Album: {projectName}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <GalleryImageLoader 
              keyName={keyName} 
              projectName={projectName} 
              selectedProject={selectedProject}
              galleryImages={displayImages} 
              setGalleryImages={setDisplayImages} 
              orginalfetchGalleryImages={fetchGalleryImages}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={handleCloseImageView} style={{ borderRadius: '10px' }}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Add Modal */}
        <Modal show={addImage} onHide={handleCloseImageAdd} size="md" centered>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">
              <i className="fa fa-plus-circle text-primary me-2"></i> Create New Album
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <AddGallery 
              setNewProjectName={setNewProjectName} 
              setNewProjectSize={setNewProjectSize} 
              setNewProjectStatus={setNewProjectStatus} 
              setNewProjectDate={setNewProjectDate} 
              setImageFile={setImageFile} 
              profileImage={profileImage} 
            />
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-end gap-2">
            <Button variant="light" onClick={handleCloseImageAdd} style={{ borderRadius: '10px' }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={postOnS3} style={{ borderRadius: '10px' }}>
              <i className="fa fa-save me-1"></i> Save Album
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Modal */}
        <Modal show={editImage} onHide={handleCloseImageEdit} size="md" centered>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">
              <i className="fa fa-edit text-primary me-2"></i> Edit Album
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <EditGallery 
              newProjectName={newProjectName} 
              newProjectStatus={newProjectStatus} 
              newProjectSize={newProjectSize} 
              newProjectDate={newProjectDate} 
              setNewProjectName={setNewProjectName} 
              setNewProjectSize={setNewProjectSize} 
              setNewProjectStatus={setNewProjectStatus} 
              setNewProjectDate={setNewProjectDate}
            />
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-end gap-2">
            <Button variant="light" onClick={handleCloseImageEdit} style={{ borderRadius: '10px' }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={editOnS3} style={{ borderRadius: '10px' }}>
              <i className="fa fa-refresh me-1"></i> Update Album
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Table of Albums */}
        <div className="table-wrapper">
          <Table hover responsive className="m-0">
            <thead className="theadStyle">
              <tr>
                <th style={{ width: '60px' }}>#</th>
                <th>Gallery Album Name</th>
                <th>Created Date</th>
                <th>Photo Count</th>
                <th style={{ width: '140px' }} className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="tbodyStyle">
              {galleryImages.length > 0 ? (
                galleryImages.map((item, index) => (
                  <tr key={index} className="trStyle">
                    <th scope="row" className="text-muted">{item.id}</th>
                    <td className="fw-semibold text-dark">{item.projectName}</td>
                    <td>
                      <span className="badge gold">{item.projectDate}</span>
                    </td>
                    <td>
                      <span className="badge primary">{item.length} Images</span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <button 
                          className="action-btn-circle" 
                          onClick={e => handleProjectView(e, item.id)}
                          title="View Album Photos"
                        >
                          <i className="fa fa-eye"></i>
                        </button>
                        <button 
                          className="action-btn-circle" 
                          onClick={e => handleProjectEdit(e, item.id)}
                          title="Edit Album Details"
                        >
                          <i className="fa fa-pencil"></i>
                        </button>
                        <button 
                          className="action-btn-circle text-danger" 
                          onClick={e => handleProjectDelete(e, item.id)}
                          title="Delete Album"
                        >
                          <i className="fa fa-trash-o"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-4 text-muted">
                    No gallery albums found. Click "Create New Gallery" to begin.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    );
}
